import type { ComponentType, SVGProps } from "react";
import React from "react";
import { NavLink } from "react-router-dom";

import { useApplicationDomain } from "~/components/application-domain-boundary/use-application-domain";
import { IconHeartbeat, IconShield } from "~/components/icon";
import { ApplicationDomain } from "~/models/dto";
import type { Record } from "~/tools/record";

import { useNavigateFromLink } from "./react/link";

const APPLICATION_DOMAIN_META: Record<
	ApplicationDomain,
	{ label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }
> = {
	[ApplicationDomain.Observability]: { label: "Observability", icon: IconHeartbeat },
	[ApplicationDomain.Security]: { label: "Security", icon: IconShield },
};

export interface ApplicationDomainSwitchLinkProps
	extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children"> {
	applicationDomain: ApplicationDomain;
	isDefault?: boolean;
}

export const ApplicationDomainSwitchLink = React.forwardRef<HTMLAnchorElement, ApplicationDomainSwitchLinkProps>(
	({ applicationDomain, isDefault, ...rest }, ref) => {
		const currentApplicationDomain = useApplicationDomain();

		const to = useNavigateFromLink("*", {
			search: (current) => ({
				...current,
				domain: isDefault ? undefined : applicationDomain,
			}),
		});

		const isActive = currentApplicationDomain === applicationDomain;
		const { label, icon: Icon } = APPLICATION_DOMAIN_META[applicationDomain];

		return (
			<NavLink
				{...rest}
				activeClassName="mode-switch-option-selected"
				aria-label={`Switch to ${label} mode`}
				innerRef={ref}
				isActive={() => isActive}
				replace
				tabIndex={isActive ? -1 : 0}
				to={to}
			>
				<Icon aria-hidden="true" className="mode-switch-option-icon" />
				<span className="mode-switch-option-label">{label}</span>
			</NavLink>
		);
	},
);
